import { ReactNode, Suspense, useCallback, useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";

import { SidebarInset, SidebarProvider, useSidebar } from "../ui/sidebar";
import { useSwitch } from "../ui/switch";

import Content from "./Content";
import Header from "./Header";
import { SidebarTab, SidebarTabContext } from "./sidebar-tab-context";
import { SidebarProps } from "./Sidebar/types";

import { ContentNavbar } from "@/components/ContentNavbar";
import LoadingSpinner from "@/components/LoadingSpinner";
import NewFeatureModal from "@/components/NewFeatureModal";
import { StructuredSearchProvider } from "@/components/StructuredSearch";
import AppSidebar from "@/components/SidebarSearchLayout/Sidebar";
import { WorkspaceStructuredSearchBootstrap } from "@/components/SidebarSearchLayout/WorkspaceStructuredSearchBootstrap";
import { TooltipProvider } from "@/components/ui/Tooltip";
import { FloatingWidget } from "@/components/VibeChat/widgets/FloatingWidget";
import { useLocalStorageState } from "@/hooks";
import { LOCAL_STORAGE_KEYS } from "@/hooks/useLocalStorageState";
import { useSessionEndTime } from "@/hooks/useSessionEndTime";
import { cn } from "@/lib/utils";
import SmartTableLoadingShell from "@/routes/smart-table/components/SmartTablePage/SmartTableLoadingShell";
import {
  isWorkspaceAnalyticsPathname,
  isWorkspaceRequestLogsTablePathname,
} from "@/utils/workspacePathMatchers";

const WorkspaceRouteSuspenseFallback = ({ pathname }: { pathname: string }) => {
  if (pathname.includes("/smart-tables/")) {
    return <SmartTableLoadingShell />;
  }

  return (
    <div className="flex h-full items-center justify-center">
      <LoadingSpinner />
    </div>
  );
};

const SidebarSearchLayoutInner = ({
  children,
  requestsOn,
  onRefresh,
  onHistoryPage,
  groupId,
}: {
  children: ReactNode;
} & Pick<SidebarProps, "requestsOn" | "onRefresh"> & {
    onHistoryPage: boolean;
    groupId: string | undefined;
  }) => {
  const location = useLocation();
  const isRequestsAnalyticsSurface =
    isWorkspaceAnalyticsPathname(location.pathname) ||
    isWorkspaceRequestLogsTablePathname(location.pathname);

  return (
    <>
      <NewFeatureModal />
      <AppSidebar requestsOn={requestsOn} onRefresh={onRefresh} />
      <SidebarInset className={cn("relative", isRequestsAnalyticsSurface && "overflow-hidden")}>
        <div className="flex h-full w-full">
          <div className="flex h-full min-w-0 flex-1 flex-col">
            <div className="flex flex-1 flex-col overflow-hidden bg-background">
              <Header />
              <ContentNavbar onHistoryPage={onHistoryPage} isGroup={!!groupId} />
              <div
                className={cn(
                  "min-h-0 flex-1",
                  isRequestsAnalyticsSurface ? "overflow-hidden" : "overflow-y-auto",
                )}
              >
                <Content>
                  <Suspense
                    fallback={<WorkspaceRouteSuspenseFallback pathname={location.pathname} />}
                  >
                    {children}
                  </Suspense>
                </Content>
              </div>
            </div>
          </div>
        </div>

        <FloatingWidget />
      </SidebarInset>
    </>
  );
};

const SidebarSearchLayoutContent = ({ children }: { children: ReactNode }) => {
  const onRequestPage =
    window.location.pathname.includes("/request") &&
    !window.location.pathname.includes("/request-logs");
  const onGroupPage = window.location.pathname.includes("/group");
  const onHistoryPage =
    (onRequestPage || onGroupPage) && !window.location.pathname.includes("playground");

  const { groupId } = useParams();
  const { resetSessionEndTime } = useSessionEndTime();
  const { traceId } = useParams();
  const { isSwitchOn: requestsOn, setSwitchOn } = useSwitch({
    options: ["Requests", "Traces"],
    reverseOrder: !!traceId,
  });

  const { state: sidebarState } = useSidebar();
  const [sidebarWidth] = useLocalStorageState<number>(LOCAL_STORAGE_KEYS.SIDEBAR_WIDTH, 320);

  useEffect(() => {
    const collapsedWidth = 64; // SIDEBAR_WIDTH_ICON in components/ui/sidebar.tsx
    const sidebarGap = 8;
    const padding = (sidebarState === "collapsed" ? collapsedWidth : sidebarWidth) + sidebarGap;
    // @ts-expect-error Intercom is not typed
    window.Intercom?.("update", { horizontal_padding: padding });
  }, [sidebarState, sidebarWidth]);

  const setActiveTab = useCallback(
    (tab: SidebarTab) => setSwitchOn(tab === "Requests"),
    [setSwitchOn],
  );
  const sidebarTabValue = useMemo(
    () => ({ activeTab: (requestsOn ? "Requests" : "Traces") as SidebarTab, setActiveTab }),
    [requestsOn, setActiveTab],
  );

  return (
    <StructuredSearchProvider>
      <SidebarTabContext.Provider value={sidebarTabValue}>
        <WorkspaceStructuredSearchBootstrap />
        <SidebarSearchLayoutInner
          requestsOn={requestsOn}
          onRefresh={resetSessionEndTime}
          onHistoryPage={onHistoryPage}
          groupId={groupId}
        >
          {children}
        </SidebarSearchLayoutInner>
      </SidebarTabContext.Provider>
    </StructuredSearchProvider>
  );
};

const SidebarSearchLayout = ({ children }: { children: ReactNode }) => {
  // Use localStorage to persist sidebar state
  const [sidebarOpen, setSidebarOpen] = useLocalStorageState<boolean>(
    LOCAL_STORAGE_KEYS.SIDEBAR_STATE,
    true, // default to open
  );

  return (
    <TooltipProvider>
      <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SidebarSearchLayoutContent>{children}</SidebarSearchLayoutContent>
      </SidebarProvider>
    </TooltipProvider>
  );
};

export default SidebarSearchLayout;
